import React, { useState } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import DefaultProfilePic from "../../../../assets/images/DefaultProfilePic.png";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import ProfileMenu from "./ProfileMenu";
import { getWhiteLableObject } from "../../../../config/whitelable";
import { IMAGE_CDN } from "../../../../config/config";
import { device } from "../../../atoms/Utlis/media";
import LogoutPopup from "../../../atoms/Dashboard/HomePage/LogOutPopUp";
import { clearToken } from "../../../../Redux/Slice/Auth/AuthSlice";
import { resolveImage } from "../../../../Utils/helper";

const ProfileSection: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutPopup, setLogoutPopup] = useState(false);

  const displayName =
    user?.name && user.name.length ? user.name : getWhiteLableObject().name;

  const rawAvatar = user?.image || (user as any)?.profile_image;
  const avatarSrc = rawAvatar
    ? resolveImage(rawAvatar) || `${IMAGE_CDN}/${rawAvatar}`
    : DefaultProfilePic;

  return (
    <>
      <ProfileSectionContainer
        id="profile-container"
        onClick={() => setMenuOpen((prev) => !prev)}
        $open={menuOpen}
      >
        {menuOpen && (
          <ProfileMenu
            setMenuOpen={setMenuOpen}
            menuOpen={menuOpen}
            onRequestLogout={() => setLogoutPopup(true)}
          />
        )}
        <AvatarRing>
          <ProfilePicture
            src={avatarSrc}
            alt={displayName}
          />
        </AvatarRing>
        <ProfileText>
          <ProfileName>{displayName}</ProfileName>
          <ProfileHint>Account</ProfileHint>
        </ProfileText>
        <ChevronIcon $open={menuOpen}>
          <ChevronDown size={16} />
        </ChevronIcon>
      </ProfileSectionContainer>

      {logoutPopup &&
        createPortal(
          <LogoutPopup
            Delete={() => {
              dispatch(clearToken());
              setLogoutPopup(false);
            }}
            cancel={() => setLogoutPopup(false)}
          />,
          document.body
        )}
    </>
  );
};

export default ProfileSection;

const ProfileSectionContainer = styled.div<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  padding: 6px 10px 6px 6px;
  border-radius: 999px;
  border: 1px solid
    ${(p) => (p.$open ? "rgba(201, 77, 170, 0.25)" : "rgba(23, 23, 23, 0.08)")};
  background: ${(p) => (p.$open ? "rgba(201, 77, 170, 0.06)" : "#fafafa")};
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease;
  max-width: 240px;
  z-index: 2;

  &:hover {
    border-color: rgba(201, 77, 170, 0.22);
    background: rgba(201, 77, 170, 0.04);
  }

  @media ${device.tablet} {
    max-width: none;
    padding-right: 8px;
  }
`;

const AvatarRing = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  padding: 2px;
  background: linear-gradient(
    144.01deg,
    rgba(255, 201, 81, 0.9) 0%,
    rgba(206, 86, 176, 0.9) 100%
  );
  flex-shrink: 0;
`;

const ProfilePicture = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
  display: block;
  background: #fff;
`;

const ProfileText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;

  @media ${device.tablet} {
    display: none;
  }
`;

const ProfileName = styled.span`
  color: #171717;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProfileHint = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ChevronIcon = styled.span<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  color: rgba(23, 23, 23, 0.45);
  transition: transform 0.15s ease;
  transform: rotate(${(p) => (p.$open ? "180deg" : "0deg")});
  flex-shrink: 0;

  @media ${device.tablet} {
    display: none;
  }
`;
